"use client";

import * as React from "react";
import { useTheme } from "next-themes";
import { getThemeById, applyThemeColors } from "@/lib/themes";
import type { ThemeColors } from "@/lib/themes";

interface AppearanceData {
  presetId: string;
  mode: "light" | "dark";
  customColors: {
    dark: Record<string, string>;
    light: Record<string, string>;
  };
  hoverEffects: {
    primaryHoverOpacity: number;
    secondaryHoverOpacity: number;
  };
}

export function ThemeApplicator() {
  const { resolvedTheme } = useTheme();

  React.useEffect(() => {
    if (!resolvedTheme) return;

    // Check for custom appearance data injected by server
    const appearanceEl = document.getElementById("__appearance_data");
    if (appearanceEl) {
      try {
        const data: AppearanceData = JSON.parse(appearanceEl.textContent || "");
        const colors = resolvedTheme === "dark" ? data.customColors.dark : data.customColors.light;
        applyThemeColors(colors as unknown as ThemeColors);
        // Apply hover effects
        if (data.hoverEffects) {
          document.documentElement.style.setProperty("--primary-hover-opacity", String(data.hoverEffects.primaryHoverOpacity));
          document.documentElement.style.setProperty("--secondary-hover-opacity", String(data.hoverEffects.secondaryHoverOpacity));
        }
        return;
      } catch {
        // Fall through to preset
      }
    }

    // Fallback: use preset
    const presetId = document.documentElement.dataset.themePreset || "academy-dark";
    const preset = getThemeById(presetId);
    if (!preset) return;

    const colors = resolvedTheme === "dark" ? preset.dark : preset.light;
    applyThemeColors(colors);
  }, [resolvedTheme]);

  return null;
}
